import { cn } from '@/lib/utils';
import { PANEL } from '@/lib/surface';
import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  X,
  MoreHorizontal,
  Pencil,
  Trash2,
  Users,
  Star,
  UserPlus,
  UserMinus,
  ChevronDown,
  ChevronUp,
  Ship,
  Plane,
  Truck,
  Train,
  Globe,
  AlertTriangle,
  Check,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { PageHeader, MetricCard, KPIGrid } from '@/components/common';
import {
  vendorGroupsApi,
  tmsCarriersApi,
  VendorGroup,
  VendorGroupRegion,
  ShipmentType,
  CreateVendorGroupDto,
  TmsCarrier,
} from '@/api/services';
import { getErrorMessage } from '@/api/client';
import { stagger, fadeUp } from '@/lib/animations';

// Constants
const SHIPMENT_TYPES: { value: ShipmentType; label: string; icon: React.ReactNode }[] = [
  { value: 'AIR', label: 'Air', icon: <Plane className="w-4 h-4" /> },
  { value: 'OCEAN_FCL', label: 'Ocean FCL', icon: <Ship className="w-4 h-4" /> },
  { value: 'OCEAN_LCL', label: 'Ocean LCL', icon: <Ship className="w-4 h-4" /> },
  { value: 'FTL', label: 'Truck FTL', icon: <Truck className="w-4 h-4" /> },
  { value: 'LTL', label: 'Truck LTL', icon: <Truck className="w-4 h-4" /> },
  { value: 'RAIL', label: 'Rail', icon: <Train className="w-4 h-4" /> },
  { value: 'INTERMODAL', label: 'Intermodal', icon: <Globe className="w-4 h-4" /> },
];

const REGIONS: VendorGroupRegion[] = [
  'North America',
  'South America',
  'Europe',
  'Asia Pacific',
  'Middle East',
  'Africa',
  'Global',
];

export default function VendorGroups() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState<VendorGroup[]>([]);
  const [carriers, setCarriers] = useState<TmsCarrier[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Sheet/Dialog states
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<VendorGroup | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<VendorGroup | null>(null);
  const [carrierSelectOpen, setCarrierSelectOpen] = useState(false);
  const [selectedGroupForCarriers, setSelectedGroupForCarriers] = useState<VendorGroup | null>(
    null
  );
  const [selectedCarrierIds, setSelectedCarrierIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState<CreateVendorGroupDto>({
    name: '',
    description: '',
    shipmentTypes: [],
    regions: [],
  });

  // Summary stats
  const [summary, setSummary] = useState({
    totalGroups: 0,
    activeGroups: 0,
    totalMembers: 0,
    avgMembersPerGroup: 0,
  });

  // Parse filters from URL
  const searchQuery = searchParams.get('search') || '';
  const shipmentTypeFilter = searchParams.get('shipmentType') as ShipmentType | null;
  const regionFilter = searchParams.get('region') as VendorGroupRegion | null;

  const setFilter = (key: string, value: string | undefined) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearchParams(new URLSearchParams());
  };

  const hasActiveFilters = searchQuery || shipmentTypeFilter || regionFilter;

  // Load data
  const loadGroups = useCallback(async () => {
    setLoading(true);
    try {
      const [groupsResponse, summaryResponse] = await Promise.all([
        vendorGroupsApi.getAll({
          search: searchQuery || undefined,
          shipmentType: shipmentTypeFilter || undefined,
          region: regionFilter || undefined,
        }),
        vendorGroupsApi.getSummary(),
      ]);
      setGroups(groupsResponse.data);
      setSummary(summaryResponse);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [searchQuery, shipmentTypeFilter, regionFilter]);

  const loadCarriers = useCallback(async () => {
    try {
      const response = await tmsCarriersApi.getAll({ isActive: true, limit: 500 });
      setCarriers(response.data);
    } catch (error) {
      console.error('Failed to load carriers:', error);
    }
  }, []);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  useEffect(() => {
    loadCarriers();
  }, [loadCarriers]);

  // Get carriers not in group
  const getAvailableCarriers = useCallback(
    (group: VendorGroup) => {
      const groupCarrierIds = new Set(group.carriers.map((c) => c.carrierId));
      return carriers.filter((c) => !groupCarrierIds.has(c.id));
    },
    [carriers]
  );

  // Toggle group expansion
  const toggleGroupExpand = (groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  // Handlers
  const handleCreate = () => {
    setEditingGroup(null);
    setFormData({
      name: '',
      description: '',
      shipmentTypes: [],
      regions: [],
    });
    setSheetOpen(true);
  };

  /**
   * The sheet has always supported editing — it titles itself "Edit Vendor
   * Group" and handleSave calls update() — but nothing ever put a group into
   * it. The only edit affordance navigated to /tms/vendor-groups/:id/edit,
   * which is not a route, so editing a group was impossible.
   */
  const handleEdit = (group: VendorGroup) => {
    setEditingGroup(group);
    setFormData({
      name: group.name,
      description: group.description ?? '',
      shipmentTypes: group.shipmentTypes ?? [],
      regions: group.regions ?? [],
    });
    setSheetOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name) {
      toast.error('Please provide a group name');
      return;
    }

    setSaving(true);
    try {
      if (editingGroup) {
        await vendorGroupsApi.update(editingGroup.id, formData);
        toast.success('Vendor group updated successfully');
      } else {
        await vendorGroupsApi.create(formData);
        toast.success('Vendor group created successfully');
      }
      setSheetOpen(false);
      await loadGroups();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!groupToDelete) return;

    try {
      await vendorGroupsApi.delete(groupToDelete.id);
      toast.success('Vendor group deleted successfully');
      setDeleteDialogOpen(false);
      setGroupToDelete(null);
      await loadGroups();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleAddCarriers = (group: VendorGroup) => {
    setSelectedGroupForCarriers(group);
    setSelectedCarrierIds([]);
    setCarrierSelectOpen(true);
  };

  const handleConfirmAddCarriers = async () => {
    if (!selectedGroupForCarriers || selectedCarrierIds.length === 0) return;

    try {
      await vendorGroupsApi.addCarriers(selectedGroupForCarriers.id, selectedCarrierIds);
      toast.success(`${selectedCarrierIds.length} carrier(s) added to group`);
      setCarrierSelectOpen(false);
      setSelectedGroupForCarriers(null);
      setSelectedCarrierIds([]);
      await loadGroups();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleRemoveCarrier = async (groupId: string, carrierId: string) => {
    try {
      await vendorGroupsApi.removeCarrier(groupId, carrierId);
      toast.success('Carrier removed from group');
      await loadGroups();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleSetPrimary = async (groupId: string, carrierId: string) => {
    try {
      await vendorGroupsApi.setPrimaryCarrier(groupId, carrierId);
      toast.success('Primary carrier updated');
      await loadGroups();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const toggleShipmentType = (type: ShipmentType) => {
    setFormData((prev) => ({
      ...prev,
      shipmentTypes: prev.shipmentTypes?.includes(type)
        ? prev.shipmentTypes.filter((t) => t !== type)
        : [...(prev.shipmentTypes || []), type],
    }));
  };

  const toggleRegion = (region: VendorGroupRegion) => {
    setFormData((prev) => ({
      ...prev,
      regions: prev.regions?.includes(region)
        ? prev.regions.filter((r) => r !== region)
        : [...(prev.regions || []), region],
    }));
  };

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          title="Vendor Groups"
          description="Named sets of carriers invited together to a request for quotation."
          actions={
            <Button onClick={() => navigate('/tms/vendor-groups/new')}>
              <Plus className="w-4 h-4 mr-2" />
              New Group
            </Button>
          }
        />
      </motion.div>

      {/* KPIs */}
      <motion.div variants={fadeUp}>
        <KPIGrid columns={4}>
          <MetricCard
            label="Total Groups"
            value={summary.totalGroups}
            icon={<Users className="w-5 h-5" />}
            sentiment="info"
          />
          <MetricCard
            label="Active Groups"
            value={summary.activeGroups}
            icon={<Check className="w-5 h-5" />}
            sentiment="success"
          />
          <MetricCard
            label="Total Members"
            value={summary.totalMembers}
            icon={<Truck className="w-5 h-5" />}
            sentiment="info"
          />
          <MetricCard
            label="Avg. per Group"
            value={summary.avgMembersPerGroup.toFixed(1)}
            icon={<Users className="w-5 h-5" />}
            sentiment="neutral"
          />
        </KPIGrid>
      </motion.div>

      {/* Filters */}
      <motion.div variants={fadeUp}>
        <div className={cn(PANEL, 'p-4')}>
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search groups..."
                value={searchQuery}
                onChange={(e) => setFilter('search', e.target.value || undefined)}
                className="pl-10 pr-10"
              />
              {searchQuery && (
                <button
                  onClick={() => setFilter('search', undefined)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Shipment Type Filter */}
            <Select
              value={shipmentTypeFilter || 'ALL_TYPES'}
              onValueChange={(v) => setFilter('shipmentType', v === 'ALL_TYPES' ? undefined : v)}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL_TYPES">All Types</SelectItem>
                {SHIPMENT_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    <div className="flex items-center gap-2">
                      {type.icon}
                      {type.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Region Filter */}
            <Select
              value={regionFilter || 'ALL_REGIONS'}
              onValueChange={(v) => setFilter('region', v === 'ALL_REGIONS' ? undefined : v)}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All Regions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL_REGIONS">All Regions</SelectItem>
                {REGIONS.map((region) => (
                  <SelectItem key={region} value={region}>
                    {region}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-destructive hover:text-destructive"
              >
                <X className="w-4 h-4 mr-1" />
                Clear All
              </Button>
            )}
          </div>
        </div>
      </motion.div>

      {/* Groups */}
      <motion.div variants={fadeUp}>
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : groups.length > 0 ? (
          <div className="space-y-4">
            {groups.map((group) => (
              <div key={group.id} className={cn(PANEL, 'overflow-hidden')}>
                {/* Group Header */}
                <div
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/50"
                  onClick={() => toggleGroupExpand(group.id)}
                >
                  <div className="flex items-center gap-4">
                    <button className="p-1 hover:bg-muted rounded">
                      {expandedGroups.has(group.id) ? (
                        <ChevronUp className="w-5 h-5" />
                      ) : (
                        <ChevronDown className="w-5 h-5" />
                      )}
                    </button>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">{group.name}</h3>
                        {!group.isActive && (
                          <Badge variant="outline" className="bg-neutral-100 text-neutral-600">
                            Inactive
                          </Badge>
                        )}
                      </div>
                      {group.description && (
                        <p className="text-sm text-muted-foreground mt-1">{group.description}</p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        {group.shipmentTypes?.map((type) => {
                          const typeInfo = SHIPMENT_TYPES.find((t) => t.value === type);
                          return (
                            <Badge key={type} variant="outline" className="text-xs">
                              {typeInfo?.icon}
                              <span className="ml-1">{typeInfo?.label || type}</span>
                            </Badge>
                          );
                        })}
                        {group.regions?.map((region) => (
                          <Badge
                            key={region}
                            variant="outline"
                            className="text-xs bg-primary-50 text-primary-600"
                          >
                            {region}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-2xl font-bold">{group.carriers?.length || 0}</div>
                      <div className="text-xs text-muted-foreground">carriers</div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="w-4 h-4" />
                          <span className="sr-only">Open menu</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEdit(group)}>
                          <Pencil className="w-4 h-4 mr-2" />
                          Edit Group
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleAddCarriers(group)}>
                          <UserPlus className="w-4 h-4 mr-2" />
                          Add Carriers
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive"
                          onClick={() => {
                            setGroupToDelete(group);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete Group
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                {/* Expanded Carrier List */}
                {expandedGroups.has(group.id) && (
                  <div className="border-t">
                    {group.carriers && group.carriers.length > 0 ? (
                      <div className="divide-y">
                        {group.carriers.map((member) => (
                          <div
                            key={member.id}
                            className="px-4 py-3 flex items-center justify-between hover:bg-muted/30"
                          >
                            <div className="flex items-center gap-3">
                              {member.isPrimary && (
                                <Star className="w-4 h-4 text-warning-500 fill-warning-500" />
                              )}
                              <div>
                                <div className="font-medium">{member.carrier.name}</div>
                                <div className="text-xs leading-4 text-muted-foreground">
                                  {member.carrier.code}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {!member.isPrimary && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleSetPrimary(group.id, member.carrierId)}
                                >
                                  <Star className="w-4 h-4 mr-1" />
                                  Set Primary
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive"
                                onClick={() => handleRemoveCarrier(group.id, member.carrierId)}
                              >
                                <UserMinus className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-8 text-center text-muted-foreground">
                        <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No carriers in this group</p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-3"
                          onClick={() => handleAddCarriers(group)}
                        >
                          <UserPlus className="w-4 h-4 mr-2" />
                          Add Carriers
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className={cn(PANEL, 'p-12 text-center')}>
            <AlertTriangle className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No Vendor Groups Found</h3>
            <p className="text-muted-foreground mb-4">
              {hasActiveFilters
                ? 'No groups match your filters.'
                : 'Get started by creating your first vendor group.'}
            </p>
            {!hasActiveFilters && (
              <Button onClick={handleCreate}>
                <Plus className="w-4 h-4 mr-2" />
                Create First Group
              </Button>
            )}
          </div>
        )}

        {/* Create/Edit Sheet */}
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetContent className="sm:max-w-lg overflow-y-auto">
            <SheetHeader>
              <SheetTitle>{editingGroup ? 'Edit Vendor Group' : 'New Vendor Group'}</SheetTitle>
              <SheetDescription>
                {editingGroup
                  ? 'Update the vendor group details below.'
                  : 'Create a new vendor group for organizing carriers.'}
              </SheetDescription>
            </SheetHeader>

            <div className="space-y-4 py-6">
              <div className="space-y-2">
                <Label htmlFor="name">Group Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Premium Ocean Carriers"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description..."
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label>Shipment Types</Label>
                <div className="flex flex-wrap gap-2">
                  {SHIPMENT_TYPES.map((type) => (
                    <Badge
                      key={type.value}
                      variant={formData.shipmentTypes?.includes(type.value) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => toggleShipmentType(type.value)}
                    >
                      {type.icon}
                      <span className="ml-1">{type.label}</span>
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Regions</Label>
                <div className="flex flex-wrap gap-2">
                  {REGIONS.map((region) => (
                    <Badge
                      key={region}
                      variant={formData.regions?.includes(region) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => toggleRegion(region)}
                    >
                      {region}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>

            <SheetFooter>
              <Button variant="outline" onClick={() => setSheetOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : editingGroup ? 'Update' : 'Create'}
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>

        {/* Add Carriers Dialog */}
        <Dialog open={carrierSelectOpen} onOpenChange={setCarrierSelectOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>Add Carriers to {selectedGroupForCarriers?.name}</DialogTitle>
              <DialogDescription>Select carriers to add to this vendor group.</DialogDescription>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto py-4">
              {selectedGroupForCarriers && (
                <div className="space-y-2">
                  {getAvailableCarriers(selectedGroupForCarriers).length > 0 ? (
                    getAvailableCarriers(selectedGroupForCarriers).map((carrier) => (
                      <label
                        key={carrier.id}
                        className="flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer"
                      >
                        <Checkbox
                          checked={selectedCarrierIds.includes(carrier.id)}
                          onCheckedChange={(checked) => {
                            setSelectedCarrierIds((prev) =>
                              checked
                                ? [...prev, carrier.id]
                                : prev.filter((id) => id !== carrier.id)
                            );
                          }}
                        />
                        <div className="flex-1">
                          <div className="font-medium">{carrier.name}</div>
                          <div className="text-xs leading-4 text-muted-foreground">
                            {carrier.code} {carrier.type && `• ${carrier.type}`}
                          </div>
                        </div>
                      </label>
                    ))
                  ) : (
                    <div className="text-center text-muted-foreground py-8">
                      All available carriers are already in this group.
                    </div>
                  )}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setCarrierSelectOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleConfirmAddCarriers} disabled={selectedCarrierIds.length === 0}>
                Add {selectedCarrierIds.length} Carrier{selectedCarrierIds.length !== 1 && 's'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-full">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                Delete Vendor Group
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete{' '}
                <span className="font-medium">{groupToDelete?.name}</span>? This will remove all
                carrier associations. This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </motion.div>
    </motion.main>
  );
}
